import React, { useState } from "react";
import { Formik } from "formik";
import { FormattedMessage } from "react-intl";
import {
  ActionBar,
  AlertType,
  apiErrorsContain,
  apiShape,
  browserShape,
  ButtonGroup,
  CancelButton,
  Column,
  CONFIGURATION,
  ConflictError,
  Container,
  Form,
  Heading,
  InputAlert,
  intlShape,
  isEmpty,
  Loader,
  NotFoundError,
  notifierShape,
  Row,
  SubmitButton,
  TextInput,
  userShape,
  withApi,
  withBackend,
  withBrowser,
  withFlipper,
  withNotifier,
  withUser,
  withIntl,
  handleFormikValueChange,
  SelectBox,
  htmlFormat,
  htmlLink,
  TextArea,
  PermissionType,
} from "lcm-iot-commons";
import { TenantTypeahead } from "lcm-iot-commons/client/lib/components/Tenants/TenantTypeahead";
import { UserGroupTypeahead } from "lcm-iot-commons/client/lib/components/UserGroups/UserGroupTypeahead";
import { ContextualHelp } from "lcm-iot-commons/client/lib/components/Contextuals/ContextualHelp";
import { NodeTypeahead } from "lcm-iot-commons/client/lib/components/Nodes/NodeTypehead";
import { EdgeDeviceTypes } from "./EdgeDeviceConstants";

type OwnProps = {
  api: apiShape;
  browser: browserShape;
  intl: intlShape;
  notifier: notifierShape;
  user?: userShape;
  flipper: {
    edgeDeviceLateBinding?: boolean;
  };
};

const defaultProps: Partial<OwnProps> = {
  user: undefined,
};

type Props = OwnProps & typeof defaultProps;

const THIRD_PARTY_EDGE_DEVICE_CODE = "third_party_edge_device";
const SGC500_EDGE_DEVICE_CODES = new Set([
  EdgeDeviceTypes.SGC500_AB,
  EdgeDeviceTypes.SGC500_AB_24,
  EdgeDeviceTypes.SGC500_AC,
]);

const normalizeSgc500EdgeDeviceName = (edgeDeviceName: string) =>
  edgeDeviceName?.startsWith("SGC500") ? "SGC500" : edgeDeviceName;

export function EdgeDeviceCreate({
  api,
  browser,
  intl,
  notifier,
  user,
}: Props) {
  const [errors, setErrors] = useState();
  const [formData, setFormData] = useState();

  const loadSubscriptionsWithFreeAddons = async () => {
    const subscriptionNames: $TSFixMe = [];

    const addonsResponse = await api.get(
      "/add_ons?scope=USABLE&add_on_type=connectivity,process_insights&include=addonable_name",
    );
    addonsResponse.add_ons.forEach((addon: $TSFixMe) => {
      if (addon.quantity > addon.assigned_edge_devices_count) {
        const index = subscriptionNames.findIndex(
          (obj: $TSFixMe) => obj.id === addon.addonable.id,
        );
        /* istanbul ignore next */
        if (index === -1) {
          subscriptionNames.push({
            name: addon.addonable.name,
            id: addon.addonable.id,
            externalReference: addon.external_reference,
            addons: [addon],
          });
        } else {
          /* istanbul ignore next */
          subscriptionNames[index].addons = subscriptionNames[
            index
          ].addons.concat([addon]);
        }
      }
    });
    /* istanbul ignore next */
    return subscriptionNames.filter((subscription: $TSFixMe) =>
      subscription.addons.some(
        (addon: $TSFixMe) => addon.product_code === "uplink",
      ),
    );
  };

  const loadEdgeDeviceTypes = async () => {
    const response = await api.get("/edm/edge_device/types");
    return response.edge_device_types;
  };

  const loadEdgeDeviceTypesOnSubscriptionSelect = async (
    selectedSubscription: $TSFixMe,
  ) => {
    const edgeDeviceTypes = await loadEdgeDeviceTypes();
    const filteredEdgeDeviceTypes = [];
    let isThirdPartyDevice = false;

    /* istanbul ignore next */
    const thirdPartyEdgeDeviceType = edgeDeviceTypes.find(
      (obj: $TSFixMe) => obj.code === THIRD_PARTY_EDGE_DEVICE_CODE,
    );
    /* istanbul ignore next */
    if (thirdPartyEdgeDeviceType) {
      filteredEdgeDeviceTypes.push(thirdPartyEdgeDeviceType);
    }
    isThirdPartyDevice = true;

    /* istanbul ignore next */
    const sgc500EdgeDevice = edgeDeviceTypes.find((obj: $TSFixMe) =>
      SGC500_EDGE_DEVICE_CODES.has(obj.code),
    );
    /* istanbul ignore next */
    if (sgc500EdgeDevice) {
      filteredEdgeDeviceTypes.push({
        ...sgc500EdgeDevice,
        name: normalizeSgc500EdgeDeviceName(sgc500EdgeDevice.name),
      });
    }
    /* istanbul ignore next */
    setFormData({
      // @ts-expect-error TS(2345) FIXME: Argument of type '{ selectedSubscription: any; edg... Remove this comment to see the full error message
      selectedSubscription,
      edgeDeviceTypes: filteredEdgeDeviceTypes,
      selectedEdgeDeviceType:
        filteredEdgeDeviceTypes.length === 1
          ? filteredEdgeDeviceTypes[0]
          : null,
      edgeDeviceTypeIsThirdParty: isThirdPartyDevice,
    });
  };

  async function fetchData() {
    const subscriptionsWithFreeAddons = await loadSubscriptionsWithFreeAddons();
    setFormData((prevState) => ({
      // @ts-expect-error TS(2698) FIXME: Spread types may only be created from object types... Remove this comment to see the full error message
      ...prevState,
      serialNumber: "",
      tenant: null,
      usergroup: null,
      subscriptions: subscriptionsWithFreeAddons,
      selectedSubscription: null,
    }));
  }

  React.useEffect(() => {
    fetchData();
  }, []);

  const handleOnSubmitLink = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      let { tenant, usergroup } = values;
      if (values.tenant?.new === true) {
        tenant = {
          id: (await api.post("/tenants", { name: values.tenant.name })).id,
        };
      }
      if (values.usergroup?.new === true) {
        usergroup = {
          id: (await api.post("/usergroups", { name: values.usergroup.name }))
            .id,
        };
      }

      /* istanbul ignore next */
      await api.patch("/edm/edge_devices/link", {
        serial_number: values.serialNumber,
        tenant: {
          id: tenant.id,
        },
        usergroup: {
          id: usergroup.id,
        },
        /* istanbul ignore next */
        add_on: {
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          id: formData.selectedSubscription
            ? // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
              formData.selectedSubscription.addons.find(
                (addon: $TSFixMe) => addon.product_code === "uplink",
              ).id
            : values.selectedSubscription.addons.find(
                (addon: $TSFixMe) => addon.product_code === "uplink",
              ).id,
        },
      });
      // @ts-expect-error TS(2345) FIXME: Argument of type 'never[]' is not assignable to pa... Remove this comment to see the full error message
      setErrors([]);
      notifier.showSuccess(
        intl.formatMessage({ id: "edge_device_create.success_message" }),
      );
      browser.navigateTo("/edge_devices");
    } catch (error) {
      if ((error as $TSFixMe).contains("association_not_found", "usergroup")) {
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ type: string; }[]' is not assi... Remove this comment to see the full error message
        setErrors([{ type: "association_not_found_usergroup" }]);
      } else if (
        (error as $TSFixMe).contains("taken", "name") &&
        values.tenant.id === undefined
      ) {
        actions.setErrors({
          tenant: intl.formatMessage({ id: "api.error.tenant.taken" }),
        });
      } else if (error instanceof NotFoundError) {
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ type: string; }[]' is not assi... Remove this comment to see the full error message
        setErrors([{ type: "not_found" }]);
      } else if (error instanceof ConflictError) {
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ type: string; }[]' is not assi... Remove this comment to see the full error message
        setErrors([{ type: "taken" }]);
      } else {
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ type: string; }[]' is not assi... Remove this comment to see the full error message
        setErrors([{ type: "api_error" }]);
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleOnSubmitCreate = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      let { usergroup, node } = values;
      const { selectedSubscription } = values;
      if (values.usergroup?.new === true) {
        usergroup = {
          id: (await api.post("/usergroups", { name: values.usergroup.name }))
            .id,
        };
      }
      if (values.node?.new === true) {
        node = {
          id: (await api.post("/nodes", { name: values.node.name })).id,
        };
      }

      const edgeDeviceStatusesResponse = await api.get(
        "/edm/edge_device/statuses",
      );
      const edgeDeviceStatusWithCodeUndefined =
        edgeDeviceStatusesResponse.edge_device_statuses.find(
          (obj: $TSFixMe) => obj.code === "undefined",
        );

      await api.post("/edm/edge_devices", {
        status: {
          id: edgeDeviceStatusWithCodeUndefined.id,
        },
        /* istanbul ignore next */
        add_on: {
          id: selectedSubscription.addons.find(
            (addon: $TSFixMe) => addon.product_code === "uplink",
          ).id,
        },
        /* istanbul ignore next */
        type: {
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          id: /* istanbul ignore next */ formData.selectedEdgeDeviceType
            ? // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
              formData.selectedEdgeDeviceType?.id
            : values.selectedEdgeDeviceType.id,
        },
        name: values.edgeDeviceName,
        description: values.description ?? null,
        serial_number: values.serialNumber,
        usergroup: {
          id: usergroup.id,
        },
        default_node: {
          id: node.id,
        },
      });
      // @ts-expect-error TS(2345) FIXME: Argument of type 'never[]' is not assignable to pa... Remove this comment to see the full error message
      setErrors([]);
      notifier.showSuccess(
        intl.formatMessage({ id: "edge_device_create.success_message" }),
      );
      browser.navigateTo("/edge_devices");
    } catch (error) {
      if (
        (error as $TSFixMe)?.contains?.("association_not_found", "usergroup")
      ) {
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ type: string; }[]' is not assi... Remove this comment to see the full error message
        setErrors([{ type: "association_not_found_usergroup" }]);
      } else if (error instanceof NotFoundError) {
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ type: string; }[]' is not assi... Remove this comment to see the full error message
        setErrors([{ type: "not_found" }]);
      } else if (error instanceof ConflictError) {
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ type: string; }[]' is not assi... Remove this comment to see the full error message
        setErrors([{ type: "taken" }]);
      } else {
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ type: string; }[]' is not assi... Remove this comment to see the full error message
        setErrors([{ type: "api_error" }]);
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleOnSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    if (formData.edgeDeviceTypeIsThirdParty) {
      await handleOnSubmitCreate(values, actions);
    } else {
      await handleOnSubmitLink(values, actions);
    }
  };

  const validateForm = (values: $TSFixMe) => {
    const formErrors = {};
    if (values.selectedSubscription?.id === undefined) {
      (formErrors as $TSFixMe).selectedSubscription = intl.formatMessage({
        id: "edge_device_create.error.subscription",
      });
      // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
      setErrors();
    }
    if (
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      formData.selectedEdgeDeviceType?.id === undefined &&
      values.selectedEdgeDeviceType?.id === undefined
    ) {
      (formErrors as $TSFixMe).selectedEdgeDeviceType = intl.formatMessage({
        id: "edge_device_create.error.selected_edge_device_type",
      });
      // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
      setErrors();
    }
    if (isEmpty(values.serialNumber) || isEmpty(values.serialNumber.trim())) {
      (formErrors as $TSFixMe).serialNumber = intl.formatMessage({
        id: "validation.serial_number.mandatory",
      });
      // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
      setErrors();
    }
    if (
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      !formData.edgeDeviceTypeIsThirdParty &&
      values.tenant?.id === undefined &&
      !values.tenant?.new === true
    ) {
      (formErrors as $TSFixMe).tenant = intl.formatMessage({
        id: "validation.product.tenant.mandatory",
      });
      // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
      setErrors();
    }
    if (values.usergroup?.id === undefined && !values.usergroup?.new === true) {
      (formErrors as $TSFixMe).usergroup = intl.formatMessage({
        id: "validation.user_group.mandatory",
      });
      // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
      setErrors();
    }
    if (
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      formData.edgeDeviceTypeIsThirdParty &&
      (isEmpty(values.edgeDeviceName) || isEmpty(values.edgeDeviceName.trim()))
    ) {
      (formErrors as $TSFixMe).edgeDeviceName = intl.formatMessage({
        id: "validation.name.mandatory",
      });
      // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
      setErrors();
    }
    if (
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      formData.edgeDeviceTypeIsThirdParty &&
      values.node?.id === undefined &&
      !values.node?.new === true
    ) {
      (formErrors as $TSFixMe).node = intl.formatMessage({
        id: "validation.node.mandatory",
      });
      // @ts-expect-error TS(2554) FIXME: Expected 1 arguments, but got 0.
      setErrors();
    }
    return formErrors;
  };

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting } = formProps;

    const contextHelperUserGroup = (
      <ContextualHelp
        title={intl.formatMessage({ id: "edit_permissions.user_groups" })}
        interactive
      >
        {intl.formatMessage({ id: "edge_device_create.user_group.help_text" })}
      </ContextualHelp>
    );

    const contextHelperTenant = (
      <ContextualHelp
        title={intl.formatMessage({ id: "label.tenant" })}
        interactive
      >
        {intl.formatMessage({ id: "edge_device_create.tenant.help_text" })}
      </ContextualHelp>
    );

    const contextHelperNode = (
      <ContextualHelp
        title={intl.formatMessage({ id: "nodes_list.header" })}
        interactive
      >
        {intl.formatMessage({ id: "edge_device_create.node.help_text" })}
      </ContextualHelp>
    );

    return (
      <Form {...formProps}>
        <SelectBox
          {...formProps}
          id="subscription"
          name="selectedSubscription"
          label={intl.formatMessage({ id: "edge_device_create.subscription" })}
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          options={formData.subscriptions}
          values={{
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            selectedSubscription: formData.selectedSubscription,
          }}
          handleChange={(event) => {
            handleFormikValueChange(
              {
                ...formProps,
                name: "selectedSubscription",
              },
              event.target.value,
            );
            /* istanbul ignore next */
            loadEdgeDeviceTypesOnSubscriptionSelect(event.target.value);
          }}
          required
        />
        <SelectBox
          {...formProps}
          id="edge-device-type"
          name="selectedEdgeDeviceType"
          label={intl.formatMessage({ id: "label.type" })}
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          options={formData.edgeDeviceTypes}
          values={{
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            selectedEdgeDeviceType: formData.selectedEdgeDeviceType,
          }}
          handleChange={(event) => {
            handleFormikValueChange(
              {
                ...formProps,
                name: "selectedEdgeDeviceType",
              },
              event.target.value,
            );
            /* istanbul ignore next */
            if (event.target.value) {
              /* istanbul ignore next */
              // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
              formData.edgeDeviceTypeIsThirdParty =
                event.target.value.code === "third_party_edge_device";
            }
          }}
          required
        />
        <TextInput
          {...formProps}
          id="edge-device-serial-number"
          name="serialNumber"
          label={intl.formatMessage({ id: "label.serial_number" })}
          required
        />
        {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
        {!formData.edgeDeviceTypeIsThirdParty ? (
          <TenantTypeahead
            {...formProps}
            id="edge-device-tenant"
            data-testid="tenant-typehead"
            name="tenant"
            label={intl.formatMessage({ id: "label.tenant" })}
            canCreate
            required
            user={user}
            intl={intl}
            notifier={notifier}
            api={api}
            help={contextHelperTenant}
          />
        ) : null}
        {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
        {formData.edgeDeviceTypeIsThirdParty ? (
          <TextInput
            {...formProps}
            id="edge-device-name"
            name="edgeDeviceName"
            label={intl.formatMessage({ id: "label.name" })}
            required
          />
        ) : null}
        {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
        {formData.edgeDeviceTypeIsThirdParty ? (
          <TextArea
            {...formProps}
            id="edge-device-description"
            name="description"
            label={intl.formatMessage({ id: "label.description" })}
          />
        ) : null}
        <UserGroupTypeahead
          {...formProps}
          id="edge-device-user-group"
          name="usergroup"
          data-testid="usergroup-typehead"
          label={intl.formatMessage({ id: "edit_permissions.user_groups" })}
          intl={intl}
          notifier={notifier}
          api={api}
          required
          canCreate
          help={contextHelperUserGroup}
        />
        {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
        {formData.edgeDeviceTypeIsThirdParty ? (
          <NodeTypeahead
            {...formProps}
            id="edge-device-node"
            name="node"
            data-testid="node-typehead"
            label={intl.formatMessage({ id: "label.nodes" })}
            intl={intl}
            notifier={notifier}
            api={api}
            required
            canCreate
            allowedPermissions={[PermissionType.READ, PermissionType.UPDATE]}
            help={contextHelperNode}
          />
        ) : null}
        <ButtonGroup>
          <SubmitButton
            id="edge-device-add-submit"
            fetching={isSubmitting}
            text={intl.formatMessage({
              id: "edge_device_create.submit_button",
            })}
          />
          <CancelButton
            id="edge-device-add-cancel"
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; disabled: any; intl: IntlShape... Remove this comment to see the full error message
            disabled={isSubmitting}
            intl={intl}
          />
        </ButtonGroup>
      </Form>
    );
  };

  const renderErrorMessage = () => {
    const errorReasons = [];

    if (apiErrorsContain(errors, "not_found")) {
      errorReasons.push(
        <li id="not-found" key="not_found">
          <FormattedMessage id="edge_device_create.errors.not_found" />
        </li>,
      );
    } else if (apiErrorsContain(errors, "association_not_found_usergroup")) {
      errorReasons.push(
        <li id="taken" key="taken">
          <FormattedMessage id="edge_device_create.errors.assotiation_usergroup" />
        </li>,
      );
    } else if (apiErrorsContain(errors, "taken")) {
      errorReasons.push(
        <li id="taken" key="taken">
          <FormattedMessage
            id="edge_device_create.errors.conflict"
            values={{
              ...htmlFormat,
              // @ts-expect-error TS(2322) FIXME: Type '(...chunks: any[]) => Element' is not assign... Remove this comment to see the full error message
              a: htmlLink({ href: `${CONFIGURATION}/support/tickets` }),
            }}
          />
        </li>,
      );
    } else if (apiErrorsContain(errors, "api_error")) {
      errorReasons.push(
        <li id="api-error" key="api_error">
          <FormattedMessage
            id="edge_device_create.errors.unknown"
            values={{
              ...htmlFormat,
              // @ts-expect-error TS(2322) FIXME: Type '(...chunks: any[]) => Element' is not assign... Remove this comment to see the full error message
              a: htmlLink({ href: `${CONFIGURATION}/support/tickets` }),
            }}
          />
        </li>,
      );
    }

    return errorReasons.length > 0 ? (
      <div id="edge-device-add-error" className="space-before">
        <InputAlert
          // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; message: st... Remove this comment to see the full error message
          id="edge-device-add-error-message"
          message={intl.formatMessage({
            id: "edge_device_create.errors.general",
          })}
          type={AlertType.ERROR}
        >
          <ul id="edge-device-add-error-reasons">{errorReasons}</ul>
        </InputAlert>
      </div>
    ) : null;
  };

  return user ? (
    <Loader loading={!formData}>
      <Container>
        <Row id="edge-device-add-header">
          <Column>
            <ActionBar>
              <Heading
                title={intl.formatMessage({ id: "edge_device_create.header" })}
              />
            </ActionBar>
          </Column>
        </Row>
        <Row id="edge-device-add-form">
          <Column sm="6">
            {formData ? (
              <Formik
                onSubmit={handleOnSubmit}
                validate={validateForm}
                initialValues={{ type: (formData as $TSFixMe).initialType }}
              >
                {renderForm}
              </Formik>
            ) : null}
          </Column>
        </Row>
        <Row id="edge-device-add-errors">
          <Column sm="6">{renderErrorMessage()}</Column>
        </Row>
      </Container>
    </Loader>
  ) : null;
}

EdgeDeviceCreate.defaultProps = defaultProps;

export default withFlipper(
  withApi(
    withBackend(
      // @ts-expect-error TS(2345) FIXME: Argument of type '{ (props: Readonly<Omit<Readonly... Remove this comment to see the full error message
      withUser(withBrowser(withNotifier(withIntl(EdgeDeviceCreate)))),
    ),
  ),
);
